<script lang="ts">
export default {
  name: 'CompaniesAnalysisScatter',
}
</script>
<script setup lang="ts">
import {
  ref,
  computed,
} from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { KkSelect, KkLoading, KkToggleSwitch } from '../../../components/base'
import { KkFilterButton, KkInfo } from '../../../components/ui'
import { KkFilterModal } from '../../../components/projects'
import { storeToRefs } from 'pinia'
import { useScatterStore } from '../../../stores/companies/analysis/scatter'
import type { AnalysisAxe } from '../../../stores/companies/analysis/scatter'

import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout.vue'
import CompaniesAnalysisScatterHeader from '../../../components/domains/analysis/scatter/Header.vue'
import CompaniesAnalysisScatterChart from '../../../components/domains/analysis/scatter/Chart.vue'

import { addAttributes } from '../../../modules/filterModalConst'

interface Period {
  startDate: Date | string
  endDate: Date | string
}
interface Param {
  posted_at_term: string,
  store_uuids: string[] | undefined
  brand_ids: number[] | undefined
  area_ids: number[] | undefined
  franchisee_ids: number[] | undefined
  user_company_ids: number[] | undefined
  prefectures: number[] | undefined
  site_types: number[] | undefined
  locales: string[] | undefined
  original_tag_group_a_tag_ids: number[]
  original_tag_group_b_tag_ids: number[]
  original_tag_group_c_tag_ids: number[]
  original_tag_group_d_tag_ids: number[]
  original_tag_group_e_tag_ids: number[]
}
interface RequestParam {
  companyUuid: string
  params: Param
}
interface FormValue {
  stores: string[]
  brands: number[]
  franchisees: number[]
  user_companies: number[]
  areas: number[]
  prefectures: number[]
  sites: number[]
  locales: string[]
  original_tag_group_a: number[]
  original_tag_group_b: number[]
  original_tag_group_c: number[]
  original_tag_group_d: number[]
  original_tag_group_e: number[]
}
interface Tag {
  id: string | number
  name: string
}
interface TagGroup {
  kind?: string
  label?: string
  tags: Tag[]
}
interface SearchAttributes {
  brands: TagGroup
  franchisees: TagGroup
  user_companies: TagGroup
  areas: TagGroup
  prefectures: TagGroup
  original_tag_group_a: TagGroup
  original_tag_group_b: TagGroup
  original_tag_group_c: TagGroup
  original_tag_group_d: TagGroup
  original_tag_group_e: TagGroup
  [key: string]: TagGroup
}
interface FilterItem {
  label: string
  kind: string
  tags: TagGroup[]
  type: string
  headText: string
  filterName: string
}
interface CommonQuery {
  store_uuids: string[] | undefined
  brand_ids: number[] | undefined
  area_ids: number[] | undefined
  franchisee_ids: number[] | undefined
  user_company_ids: number[] | undefined
  prefectures: number[] | undefined
  site_types: number[] | undefined
  locales: string[] | undefined
  original_tag_group_a_tag_ids: number[]
  original_tag_group_b_tag_ids: number[]
  original_tag_group_c_tag_ids: number[]
  original_tag_group_d_tag_ids: number[]
  original_tag_group_e_tag_ids: number[]
}

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const router = useRouter()

const scatterStore = useScatterStore()
const {
  rawAggs,
  pageType,
  aggsLoading,
  filteredCount
} = storeToRefs(scatterStore)
const {
  readScatters,
  setPageType
} = scatterStore

const formValues = computed<FormValue>(() => store.state.companiesSearchTagGroups.formValues)
const period = computed<Period>(() => store.state.filters.period)
const analysisAxes = computed<AnalysisAxe[]>(() => {
  return store.state.companiesAnalysisAxes.data.filter((axis: AnalysisAxe) => {
    // NOTE: インバウンドオプションが有効の場合のみ、言語別が利用可能
    if (axis.id === 'locale') return inboundOption.value

    return true
  })
})
const analysisAxesLoading = computed<boolean>(() => store.state.companiesAnalysisAxes.loading)
const searchAttributes = computed<SearchAttributes>(() => store.state.companiesSearchTagGroups.data)
const searchAttributesLoading = computed<boolean>(() => store.state.companiesSearchTagGroups.loading)
const showMoreThanEditorAuthority = computed(() => store.getters['companiesAccessibleApi/showMoreThanEditorAuthority'])
const inboundOption = computed(() => store.getters['companiesCompany/inboundOption'])
const setPeriod = (period: Period): Promise<void> => store.dispatch('filters/setPeriod', period)
const readCompaniesAnalysisAxes = (): Promise<void> => store.dispatch('companiesAnalysisAxes/readCompaniesAnalysisAxes')
const readSearchAttributes = (companyUuid: string): Promise<void> => store.dispatch('companiesSearchTagGroups/readSearchTagGroups', companyUuid)
const setFormValues = (payload: FormValue): Promise<void> => store.dispatch('companiesSearchTagGroups/setFormValues', payload)

const showFilterModal = ref<boolean>(false)
const filterItems = ref<FilterItem[]>([])
const showLabel = ref<boolean>(false)

const loading = computed<boolean>(() => aggsLoading.value)
const showFilterButton = computed<boolean>(() => !searchAttributesLoading.value)
const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const currentAnalysisTypeId = computed({
  get: (): string => pageType.value.name,
  set: (value: string): void => {
    const pageType = analysisAxes.value.find(i => i.name === value)
    if (pageType === undefined) return
    router.push({ query: { ...route.query, fields: pageType.fields } })
  },
})
const analysisTypeOptions = computed(() => {
  const obj: { [type: string]: string } = {}
  analysisAxes.value.forEach(v => obj[v.name] = v.name)
  return obj
})

const requestParams = computed<RequestParam>(() => {
  return {
    companyUuid: currentCompanyUuid.value,
    params: {
      posted_at_term: `${moment(period.value.startDate).format('YYYY/MM/DD')} - ${moment(period.value.endDate).format('YYYY/MM/DD')}`,
      ...commonQuery()
    }
  }
})

const changePeriod = (value: Period): void => {
  if (loading.value) return
  setPeriod(value)
  fetchData()
}
const fetchData = async (): Promise<void> => {
  await readScatters(requestParams.value)
}
const createFilter = (): void => {
  const attributes = [
    searchAttributes.value.sites,
    searchAttributes.value.brands,
    searchAttributes.value.areas,
    searchAttributes.value.franchisees,
    searchAttributes.value.user_companies,
  ]

  if(inboundOption.value) attributes.unshift(searchAttributes.value.locales)

  if(searchAttributes.value.original_tag_group_a) attributes.push(searchAttributes.value.original_tag_group_a)
  if(searchAttributes.value.original_tag_group_b) attributes.push(searchAttributes.value.original_tag_group_b)
  if(searchAttributes.value.original_tag_group_c) attributes.push(searchAttributes.value.original_tag_group_c)
  if(searchAttributes.value.original_tag_group_d) attributes.push(searchAttributes.value.original_tag_group_d)
  if(searchAttributes.value.original_tag_group_e) attributes.push(searchAttributes.value.original_tag_group_e)

  attributes.push(searchAttributes.value.prefectures)
  attributes.push(searchAttributes.value.stores)

  filterItems.value = attributes.map((attr) => {
    if (attr && attr.kind) {
      return addAttributes(attr, attr.kind)
    }
  })
}
const formInitialValues = (): FormValue => {
  return {
    stores: [],
    sites: [],
    brands: [],
    franchisees: [],
    areas: [],
    user_companies: [],
    prefectures: [],
    locales: [],
    original_tag_group_a: [],
    original_tag_group_b: [],
    original_tag_group_c: [],
    original_tag_group_d: [],
    original_tag_group_e: []
  }
}
const commonQuery = (): CommonQuery => {
  const { stores, brands, areas, franchisees, user_companies, prefectures, sites, locales } = formValues.value
  return {
    store_uuids: stores,
    brand_ids: brands,
    site_types: sites,
    franchisee_ids: franchisees,
    user_company_ids: user_companies,
    area_ids: areas,
    prefectures: prefectures,
    locales: locales,
    original_tag_group_a_tag_ids: formValues.value.original_tag_group_a,
    original_tag_group_b_tag_ids: formValues.value.original_tag_group_b,
    original_tag_group_c_tag_ids: formValues.value.original_tag_group_c,
    original_tag_group_d_tag_ids: formValues.value.original_tag_group_d,
    original_tag_group_e_tag_ids: formValues.value.original_tag_group_e,
  }
}
/**
 * 絞り込み条件の変更
 */
const handleSubmit = (): void => {
  showFilterModal.value = false
}

const handleReset = (): void => {
  setFormValues(formInitialValues())
  readScatters(requestParams.value)
  showFilterModal.value = false
}

const handleChangeFilterModalCheckBox = ({ kind, id }: { kind: keyof FormValue, id: number | string }): void => {
  const newFormValues: FormValue = Object.assign({}, formValues.value)
  if ((kind === 'stores' || kind === 'locales') && typeof id === 'string') {
    if (newFormValues[kind].includes(id)) {
      newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
    } else {
      newFormValues[kind].push(id)
    }
  } else if ((kind !== 'stores' && kind !== 'locales' && typeof id === 'number')) {
    if(newFormValues[kind].includes(id)) {
      newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
    } else {
      newFormValues[kind].push(id)
    }
  }
  setFormValues(newFormValues)
  readScatters(requestParams.value)
}

const initParams = (fields: string): void => {
  setPageType(analysisAxes.value, fields || 'store_id')
}
onBeforeRouteUpdate((to, from): void => {
  initParams(to.query.fields as string)
  fetchData()
})

const load = async (): Promise<void> => {
  await Promise.all([
    readCompaniesAnalysisAxes(),
    readSearchAttributes(currentCompanyUuid.value)
  ])
  setFormValues(formInitialValues())
  createFilter()
  initParams(route.query.fields as string)
  readScatters(requestParams.value)
  fetchData()
}

load()
</script>
<template>
  <CompaniesAnalysisLayout
    :company-uuid="$route.params.company_uuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <!-- ヘッダー -->
    <template #header>
      <CompaniesAnalysisScatterHeader
        :period="period"
        @change-period="changePeriod"
      >
        <template v-if="!analysisAxesLoading" #select>
          <KkSelect v-model="currentAnalysisTypeId" class="analysisTypeSelect" :options="analysisTypeOptions" />
          <KkFilterButton v-if="showFilterButton" class="filterButton" @click="showFilterModal = true" />
        </template>
      </CompaniesAnalysisScatterHeader>
    </template>
    <!-- 散布図 -->
    <template #graph>
      <KkInfo class="note">
        {{ t('pages.companies.analysis.scatter.note') }}
      </KkInfo>
      <KkToggleSwitch
        id="showLabel"
        class="toggle"
        :label="t('pages.companies.analysis.scatter.showLabel')"
        :model-value="showLabel"
        @update:model-value="(value: boolean) => showLabel = value"
      />
      <KkLoading v-if="loading" />
      <CompaniesAnalysisScatterChart
        v-else
        :raw-aggs="rawAggs"
        :page-type="pageType"
        :show-label="showLabel"
      />
    </template>
  </CompaniesAnalysisLayout>
  <KkFilterModal
    v-if="showFilterModal"
    :is-visible="showFilterModal"
    :filter-items="filterItems"
    :form-values="formValues"
    :total-count="filteredCount"
    @close="showFilterModal = false"
    @submit="handleSubmit"
    @reset="handleReset"
    @change-check-box="handleChangeFilterModalCheckBox"
  />
</template>
<style lang="scss" scoped>
.analysisTypeSelect {
  width: 320px;
}
.filterButton {
  margin-left: 12px;
}
.note {
  margin-bottom: 32px
}
.toggle {
  margin-bottom: 48px;
}
</style>
